import { Base_Date_Format_Options as Date_Format_Options } from '@/core/src/constants/prop';
import {
  convertTimeByModule,
  displayCascade,
  displayText,
  displayUser,
} from '@/core/src/operation/propUtils';
import { ApiResponse } from '../@types/ApiResponse';
import { CREATE_TIME, CREATOR, PASS_ANY, STATUS_PROP } from '../constants/formula';
import { QUOTE_SYS_PROP } from './constant';
import { getDefaultStatus, getDefaultStatusProp } from './status';

import dayjs from 'dayjs';
import { OrgGroups } from '../@types/ApiResponse/OrgGroups';
import { getDepartmentByUser } from './org';
import { uniq, values } from 'ramda';
import { conditionMatchPropsData, propIsNull } from '../operation';
import { takeOne, toArray, extractFormulaInput } from './util';

export const getTempPropList = (
  templateList: ApiResponse.CurrentUser.TemplateInfo[],
  tempId: string,
) => {
  const temp = templateList.find((x) => x.template_id === tempId);
  if (!temp) {
    return [];
  }
  return temp.prop || [];
};

/** 获取必填的属性 */
export const hasRequiredProp = (propConfig: ApiResponse.CurrentUser.TemplateProp[]) => {
  return !!propConfig.filter((x) => {
    return x && x.type && x.required && x.hide !== true;
  }).length;
};

/**
 * 获取节点默认模板属性
 * @param {object} template 模板配置
 * @param {array} matchProps 条件匹配数据
 * @param {Object} userMap 用户map
 * @param {string} creator 创建者id
 * @param {string} userId 当前用户id
 * @param {number} createTime 创建时间
 * @param {array} initProp 需要在初始化时赋值
 */
export const getDefaultProp = (
  template: ApiResponse.CurrentUser.TemplateInfo,
  matchProps: null | any[],
  userMap: Record<string, ApiResponse.OrgUser.OrgUserItem>,
  creator: string,
  userId: string,
  createTime: number | undefined,
  initProp: null | any[] = null,
  title: string = '',
  departmentMap: Record<string, ApiResponse.OrgGroups.OrgDepartmentNodeInfo> = {},
) => {
  // 默认值
  let defaultProp: any = template.prop.map((propInfo, index) => {
    try {
      if (propInfo.type === 'user') {
        let defaultValue = propInfo.defaultValue;

        if (propInfo.defaultFormula) {
          if (propInfo.defaultFormula.type === 'const') {
            defaultValue = propInfo.defaultFormula.const as any;
          } else if (propInfo.defaultFormula.type === 'formula') {
            const input = extractFormulaInput(propInfo.defaultFormula);

            if (input?.type === 'creator') {
              defaultValue = 'creator';
            }
            if (input?.type === 'updater') {
              defaultValue = 'updater';
            }
          }
        }

        /**
         * NOTE:
         * - 不支持引用其他人员属性
         */
        if (defaultValue) {
          if (defaultValue === 'creator') {
            // 创建者
            return propInfo.multiple ? [creator] : creator;
          } else if (defaultValue === 'updater') {
            // 更新者
            return propInfo.multiple ? [userId] : userId;
          } else {
            // 用户id
            return propInfo.multiple ? [defaultValue] : defaultValue;
          }
        }
      }
      // 时间
      if (propInfo.type === 'datetime' || propInfo.type === 'date') {
        const genTime = (v?: any) => {
          const dateModule =
            Date_Format_Options.find((x) => x.value === propInfo.dateFormat)?.module ||
            (propInfo.type === 'date' ? 'day' : 'min');

          return convertTimeByModule(dayjs(v), dateModule);
        };

        let defaultValue: string | number | undefined = propInfo.defaultValue;

        /**
         * 新的默认值字段
         *
         * NOTE:
         * - 不支持引用其他属性
         * - 不支持引用状态属性
         */

        if (propInfo.defaultFormula) {
          if (propInfo.defaultFormula.type === 'const') {
            defaultValue = propInfo.defaultFormula.const;
          } else if (propInfo.defaultFormula.type === 'formula') {
            const input = extractFormulaInput(propInfo.defaultFormula);

            if (input?.type === 'create_time') defaultValue = 'createTime';
          }
        }
        // 老的默认值字段
        else if (defaultValue) {
          if (defaultValue === 'createTime') {
            return createTime
              ? genTime(createTime.toString().length === 13 ? createTime : createTime * 1000)
              : genTime();
          } else if (defaultValue === 'updateTime') {
            return genTime();
          } else if (typeof defaultValue === 'number') return defaultValue;
        }
      }
      // 选值
      if (propInfo.type === 'tag' || propInfo.type === 'enum') {
        const defaultValue =
          propInfo.defaultValue ||
          (propInfo.defaultFormula.type === 'const' ? propInfo.defaultFormula.const : undefined);
        if (defaultValue) return propInfo.multiple ? [defaultValue] : defaultValue;
      }
      // 数字
      if (
        (propInfo.type === 'number' || propInfo.type === 'currency') &&
        !propIsNull(propInfo.defaultValue) &&
        propInfo.defaultFormula.type === 'const' &&
        Number.isFinite(Number(propInfo.defaultFormula.const))
      ) {
        return Number(propInfo.defaultFormula.const);
      }
      // 文本
      if (propInfo.type === 'text' && propInfo.defaultFormula.type === 'const') {
        return propInfo.defaultFormula.const;
      }
      if (propInfo.type === 'department') {
        if (propInfo.departmentConfig?.defaultValue) {
          const value = uniq(
            toArray(propInfo.departmentConfig?.defaultValue)
              .map((v) => {
                if (v.startsWith('group_')) return v.replace('group_', '');
                if (v.startsWith('dynamicDepartment_')) {
                  const [, propStr, levelStr] = v.split('_');
                  const level = Number(levelStr);
                  if (propStr === 'creator')
                    return getDepartmentByUser(userMap[creator], values(departmentMap), level).map(
                      (d) => d.id,
                    );
                }
                return null;
              })
              .filter(Boolean)
              .flat(),
          );
          return propInfo.multiple ? value : takeOne(value);
        }
      }
    } catch (error) {
      return null;
    }
    return null;
  });

  // 创建者条件的匹配
  defaultProp = conditionMatchPropsData({
    handleDataIndex: -1,
    propData: defaultProp,
    userMap,
    template,
    matchConfig: [],
    creator: creator,
    createTime: createTime,
    currentUserId: userId,
  })[0];

  if (title) {
    // 标题作为匹配条件
    defaultProp = conditionMatchPropsData({
      handleDataIndex: -3,
      propData: defaultProp,
      userMap,
      template,
      matchConfig: [],
      creator: creator,
      title: title,
      changedFields: [],
      createTime: createTime,
      currentUserId: userId,
    })[0];
  }

  // 新增时赋值
  if (initProp) {
    initProp.map((x, i) => {
      if (x !== null) {
        defaultProp[i] = x;
      }
    });
  }
  return defaultProp;
};

interface IInjectGetter {
  getCurrentPosition: () => Promise<{
    address: string;
    city: string;
    district: string;
    latitude: number;
    longitude: number;
    name: string;
    province: string;
  }>;
}

type PiNodeProps = PiNode['prop'];

export function getDefaultTempProp(
  newProps: Partial<PiNodeProps>,
  template: ApiResponse.CurrentUser.TemplateInfo,
  userIdOfcurrentUser: { userid: string },
  // 传node就是编辑
  node: PiNode | null,
  userMap: Record<string, ApiResponse.OrgUser.OrgUserItem>,
  departmentNodes: OrgGroups.OrgDepartmentNodeInfo[],
  initData?: Partial<{
    initProp: any[];
    initStatus: number;
    initStatusProp: any[];
    initSysTag: any[];
    initSysCascade: any;
  }> | null,
): PiNodeProps;

export function getDefaultTempProp(
  newProps: Partial<PiNodeProps>,
  template: ApiResponse.CurrentUser.TemplateInfo,
  userIdOfcurrentUser: { userid: string },
  // 传node就是编辑
  node: PiNode | null,
  userMap: Record<string, ApiResponse.OrgUser.OrgUserItem>,
  departmentNodes: OrgGroups.OrgDepartmentNodeInfo[],
  initData: Partial<{
    initProp: any[];
    initStatus: number;
    initStatusProp: any[];
    initSysTag: any[];
    initSysCascade: any;
  }> | null,
  injectGetter?: any,
  // departmentNodes?: any[],
): Promise<PiNodeProps>;

//获取模板默认值 { _sys_temp:[], _sys_task_status:{} }
export function getDefaultTempProp(
  newProps: Partial<PiNodeProps>,
  template: ApiResponse.CurrentUser.TemplateInfo,
  userIdOfcurrentUser: { userid: string },
  // 传node就是编辑
  node: PiNode | null = null,
  userMap: Record<string, ApiResponse.OrgUser.OrgUserItem> = {},
  departmentNodes: OrgGroups.OrgDepartmentNodeInfo[] = [],
  initData: Partial<{
    initProp: any[];
    initStatus: number;
    initStatusProp: any[];
    initSysTag: any[];
    initSysCascade: any;
  }> | null = null,
  injectGetter?: IInjectGetter,
  // departmentNodes?: any[],
): PiNodeProps | Promise<PiNodeProps> {
  // _sys_temp
  const _sys_temp: [string, any[]] = [] as any;

  // _sys_temp[0]
  const defaultTempId = template.template_id;
  _sys_temp.push(defaultTempId);

  // _sys_temp[1]
  const creator = node ? node.prop._sys_creator : userIdOfcurrentUser.userid;

  /**
   * FIXME: @yuan7
   * 1. _sys_create_time 字段不存在, 是 _sys_createTime
   * 2. _sys_createTime 单位是秒，而不是毫秒
   */
  const createTime = node ? node.prop._sys_create_time : dayjs().valueOf();
  const defaultProps = getDefaultProp(
    template,
    [],
    userMap,
    creator,
    userIdOfcurrentUser.userid,
    createTime,
    initData?.initProp || [],
    undefined,
    departmentNodes.reduce(
      (r, n) => ({
        ...r,
        [n.id]: n,
      }),
      {},
    ),
  );
  _sys_temp.push(defaultProps);

  // status
  const defaultStatusIndex = initData?.initStatus ?? getDefaultStatus(template);
  // status props
  const editNodeStatusProp = node ? node?.metadata?.e?._sys_task_status?.prop || [] : [];
  const defaultStatusProp = getDefaultStatusProp(
    {
      tempProp: defaultProps,
      tempStatusProp: editNodeStatusProp,
      creator: creator,
      createTime: createTime,
      historyStatusInfo: [],
    },
    defaultStatusIndex,
    template,
    userIdOfcurrentUser.userid,
    initData?.initStatusProp || [],
    userMap,
    departmentNodes,
  );

  const _sys_task_status: PiNodeMetaData['e']['_sys_task_status'] = {
    index: defaultStatusIndex,
    prop: defaultStatusProp,
    sender: userIdOfcurrentUser.userid,
    sendTime: createTime!,
  };

  // 设置newprops
  newProps['_sys_temp'] = _sys_temp;
  newProps['_sys_task_status'] = _sys_task_status;
  if (initData?.initSysTag && Array.isArray(initData.initSysTag)) {
    const initTag: Record<any, any> = {};
    initData.initSysTag.map((x) => {
      initTag[x] = userIdOfcurrentUser.userid;
    });
    // newProps(initTag); @biaobiao 这么调用是为什么？newProps不是个{} ？
    newProps['_sys_tag'] = initTag;
  }
  if (initData?.initSysCascade) {
    newProps['_sys_cascade'] = initData?.initSysCascade;
  }

  // 重载，获取异步数据
  /**
   * NOTE: 当定位属性没有值的时候，默认使用当前定位
   */
  if (typeof injectGetter?.getCurrentPosition === 'function') {
    return (async () => {
      let currentPosition:
        | Awaited<ReturnType<NonNullable<IInjectGetter['getCurrentPosition']>>>
        | undefined = undefined;

      for (let i = 0; i < template.prop.length; i++) {
        const prop = template.prop[i];

        if (prop.type === 'positioning' && propIsNull(defaultProps[i])) {
          if (!currentPosition) {
            currentPosition = await injectGetter?.getCurrentPosition();
          }
          newProps['_sys_temp']![1][i] = [currentPosition.name];
          if (!newProps['_sys_location']) {
            newProps['_sys_location'] = {};
          }
          newProps['_sys_location'][i] = [
            {
              add: currentPosition.address,
              city: currentPosition.city,
              dist: currentPosition.district,
              lat: currentPosition.latitude,
              lng: currentPosition.longitude,
              prov: currentPosition.province,
            },
          ];
        }
      }
      return newProps as PiNodeProps;
    })();
  }

  return newProps as PiNodeProps;
}

/** 获取引用属性的引用配置 */
export const getQuoteProp = (
  prop: ApiResponse.CurrentUser.TemplateProp | '-3',
  tempMap: Record<string, ApiResponse.CurrentUser.TemplateInfo>,
) => {
  if (prop === '-3') return null;
  if (!prop?.type) return null;
  if (prop.type !== 'quote') return prop;

  const matching = prop?.matchingProp?.[0];
  if (!matching) return null;
  if (matching === '-3') {
    return '-3';
  }
  const [matchTempId, matchPropIndex] = matching.split('-');
  const quoteProp = tempMap[matchTempId]?.prop?.[Number(matchPropIndex)];
  if (!quoteProp || !quoteProp.type) return null;
  return quoteProp;
};

/**
 * 获取引用属性引用源头的属性配置
 * 1. 引用属性外普通属性
 * 2. 匹配/引用 统计
 * 3. 特殊定义 QUOTE_SYS_PROP:{-3:{ index, name, type, multiple }}
 * 4. null
 */
export const getQuoteOriginProp = (
  prop: ApiResponse.CurrentUser.TemplateProp | null | keyof typeof QUOTE_SYS_PROP,
  tempMap: Record<string, ApiResponse.CurrentUser.TemplateInfo>,
  findList: string[] = [],
  /**
   *
   * 引用统计是否直接返回
   *
   * 和getMatchingOriginProp 等的差异 其实就是 要不要在引用统计时就return 还是 需要继续找引用源的配
   * 通过这个配置区分
   */
  isReturnWithConditionMatching: boolean = true,
): ApiResponse.CurrentUser.TemplateProp | null | keyof typeof QUOTE_SYS_PROP => {
  // 特殊定义
  if (typeof prop === 'string') return prop in QUOTE_SYS_PROP ? prop : null;
  // 属性不存在
  if (!prop || !prop?.type) return null;
  // 匹配/引用 统计
  // 仅在计数时，不想继续查找被引用属性
  if (prop.conditionMatching && [1, 2, 5].includes(prop.matchingType)) {
    if (isReturnWithConditionMatching) {
      return prop;
    }
  }
  // 非引用属性 直接返回
  if (prop.type !== 'quote') return prop;

  // 查找下一层引用
  const matching = prop?.matchingProp?.[0];
  if (!matching) return null;

  // 防止 loop
  if (findList.includes(matching)) {
    console.error('getQuoteOriginProp loop', findList, matching);
    return null;
  }

  // 判断特殊定义 再拆字符判断
  if (matching in QUOTE_SYS_PROP) {
    return matching as keyof typeof QUOTE_SYS_PROP;
  }

  const [matchTempId, matchPropIndex] = (matching as string).split('-');

  const quoteProp = tempMap?.[matchTempId]?.prop?.[Number(matchPropIndex)];
  const result = getQuoteOriginProp(quoteProp, tempMap, [...findList, matching]);
  if (typeof result === 'string' || !result) return result;
  if (
    prop.conditionMatching &&
    [3, 4].includes(prop.matchingType) &&
    !['date', 'datetime'].includes(result.type)
  ) {
    if (isReturnWithConditionMatching) {
      return prop;
    }
  }
  return result;
};

// 返回类型 和 类型的部分配置
// 只处理到引用统计 不继续往下
// 所以不支持numberextend
export const getQuoteOriginPropInfo = (
  prop: Parameters<typeof getQuoteOriginProp>[0],
  tempMap: Parameters<typeof getQuoteOriginProp>[1],
) => {
  const originalProp = getQuoteOriginProp(prop, tempMap);

  const res: {
    type: ApiResponse.CurrentUser.propType | null;
    multiple: boolean;
    cascade: ApiResponse.CurrentUser.cascadeSetting | null;
    originalProp: ReturnType<typeof getQuoteOriginProp>;
    numberExtend: ApiResponse.CurrentUser.numberExtend;
    dateFormat: ApiResponse.CurrentUser.TemplateProp['dateFormat'];
    conditionMatching?: boolean;
  } = {
    type: null,
    multiple: false,
    cascade: null,
    originalProp,
    numberExtend: {
      unit: '',
      precision: 0,
      numericalFormat: 0,
      unitPosition: 0,
    },
    dateFormat: 'YYYYMMDD',
  };

  if (!originalProp) {
    res.type = null;
  }
  // 在QUOTE_SYS_PROP里设置的特殊标识
  else if (typeof originalProp === 'string') {
    res.type = (QUOTE_SYS_PROP[originalProp]?.type as ApiResponse.CurrentUser.propType) || 'text';
    res.multiple =
      (QUOTE_SYS_PROP[originalProp] as Partial<ApiResponse.CurrentUser.TemplateProp>)?.multiple ||
      false;
  }
  // 引用统计
  else if (originalProp.conditionMatching && originalProp.matchingType > 0) {
    res.type = 'number';
    res.multiple = false;
  }
  // 引用公式
  else if (originalProp.type === 'formula') {
    res.type = originalProp.formulaFormat === 0 ? 'number' : 'text';
    res.multiple = false;
    // 数字公式 配置
    if (originalProp.formulaFormat === 0) {
      res.numberExtend = originalProp.number;
    }
  }
  // 其他属性
  else {
    res.type = originalProp.type;
    res.multiple = originalProp.multiple || false;

    if (originalProp.type === 'cascade') {
      res.cascade = originalProp.cascade;
    }

    if (originalProp.type === 'number') {
      res.numberExtend = originalProp.number;
    }
    if (originalProp.type === 'date' || originalProp.type === 'datetime') {
      res.dateFormat = originalProp.dateFormat;
    }
    if (['enum', 'tag'].includes(originalProp.type)) {
      res.conditionMatching = originalProp.conditionMatching;
    }
  }

  return res;
};

export const getMatchingOriginProp = (
  prop:
    | ApiResponse.CurrentUser.TemplateProp
    | '-3' // 标题
    | null,
  tempMap: Record<string, ApiResponse.CurrentUser.TemplateInfo>,
  findList: string[] = [],
):
  | ApiResponse.CurrentUser.TemplateProp
  | null
  | (typeof QUOTE_SYS_PROP)[keyof typeof QUOTE_SYS_PROP]
  | { type: string } => {
  // 状态
  // 属性不存在
  if (!prop || !tempMap) return null;
  if (typeof prop === 'string' && prop in QUOTE_SYS_PROP) return QUOTE_SYS_PROP[prop];
  if (!(typeof prop === 'object')) return null;
  // 属性不存在
  if (!prop?.type) return null;
  // 非引用属性 直接返回
  if (prop.type !== 'quote') return prop;
  // 引用 统计计数
  if (prop.matchingType === 5) return { type: 'number' };
  // 查找下一层引用
  const matching = prop?.matchingProp?.[0];
  if (!matching) return null;
  if (matching in QUOTE_SYS_PROP)
    return {
      ...QUOTE_SYS_PROP[matching as keyof typeof QUOTE_SYS_PROP],
      quoteTempId: prop.matchingTemplate,
    };
  // 防止 loop
  if (findList.includes(matching)) {
    console.error('getMatchingOriginProp loop', findList, matching);
    return null;
  }
  const [matchTempId, matchPropIndex] = matching.split('-');
  const quoteProp = tempMap[matchTempId]?.prop?.[Number(matchPropIndex)];
  return getMatchingOriginProp(quoteProp, tempMap, [...findList, matching]);
};

const isNil = (v: unknown) => {
  if (typeof v === 'undefined') {
    return true;
  }
  if (v === null) return true;
  return false;
};

/**
 * @deprecated
 */
export const updateDefaultProp = (options: {
  nodeMetadata?: PiNodeMetaData;
  templateConfig: ApiResponse.CurrentUser.TemplateInfo;
  orgId: string;
  editedProps: number[];
  spaceUserList: any;
}) => {
  if (!options.nodeMetadata) return;
  const updateProps: {
    index: number;
    value: any;
  }[] = [];
  const template: ApiResponse.CurrentUser.TemplateInfo = options.templateConfig;
  const tempPropData = options.nodeMetadata.e._sys_temp[1];

  if (template && Array.isArray(template.prop) && template.prop.length) {
    template.prop.forEach((prop, index) => {
      // 过滤无效属性
      if (!prop) return;
      if (!prop.type) return;

      // 过滤已经编辑过的属性
      if (options.editedProps.includes(index)) return;
      // 过滤已经有值的属性
      if (!isNil(tempPropData[index])) return;

      if (prop.defaultFormula) {
        if (prop.defaultFormula.type === 'prop') {
          if (
            template.prop[prop.defaultFormula.prop] &&
            !propIsNull(tempPropData[prop.defaultFormula.prop])
          ) {
            const formulaValue = Array.isArray(tempPropData[prop.defaultFormula.prop])
              ? tempPropData[prop.defaultFormula.prop]
              : [tempPropData[prop.defaultFormula.prop]];

            updateProps.push({
              index: index,
              value:
                prop.type === 'text'
                  ? displayText(formulaValue)
                  : prop.multiple && Array.isArray(formulaValue)
                    ? formulaValue[0]
                    : formulaValue,
            });
          }
        } else if (prop.defaultFormula.type === 'formula') {
          const propIndex =
            'input' in prop.defaultFormula.formula
              ? prop.defaultFormula.formula.input[0].prop
              : prop.defaultFormula.formula.type === 'cas_prop'
                ? prop.defaultFormula.formula.prop
                : -1;
          if (~propIndex && template.prop[propIndex] && !propIsNull(tempPropData[propIndex])) {
            let formulaValue = null;
            if (prop.defaultFormula.formula.op === 'nick_name') {
              formulaValue = displayUser(
                tempPropData[propIndex],
                options.spaceUserList[options.orgId],
                ';',
              );
            } else if (prop.defaultFormula.formula.type === 'cas_prop') {
              formulaValue = displayCascade(
                options.nodeMetadata?.e._sys_cascade?.[propIndex],
                '/',
                template.prop[propIndex].hideRoutes,
              );
            }
            if (!propIsNull(formulaValue)) {
              formulaValue = Array.isArray(formulaValue) ? formulaValue : [formulaValue];
              updateProps.push({
                index: index,
                value:
                  prop.type === 'text'
                    ? displayText(formulaValue)
                    : prop.multiple
                      ? formulaValue
                      : formulaValue[0],
              });
            }
          }
        } else if (!propIsNull(prop.defaultFormula.const)) {
          updateProps.push({
            index: index,
            value: prop.defaultFormula.const,
          });
        }
      } else if (
        (prop.type === 'date' || prop.type === 'datetime') &&
        prop.defaultValue &&
        typeof prop.defaultValue === 'string' &&
        (prop.defaultValue.startsWith('prop') || prop.defaultValue.startsWith('status'))
      ) {
        const propDateFormat =
          prop.dateFormat || (prop.type === 'date' ? 'YYYY/MM/DD' : 'YYYY/MM/DD HH:mm');
        const dateModule =
          Date_Format_Options.find((x) => x.value === propDateFormat)?.module || 'day';
        const defaultIndex = parseInt(prop.defaultValue.split('-')[1]);
        const defaultValue = prop.defaultValue.startsWith('status')
          ? options.nodeMetadata?.e?._sys_task_status.prop[defaultIndex]
          : tempPropData[defaultIndex];
        if (defaultValue && dayjs(defaultValue).isValid()) {
          updateProps.push({
            index: index,
            value: convertTimeByModule(dayjs(defaultValue), dateModule),
          });
        }
      }
    });
    return updateProps;
  }
};

/**
 * 将 defaultValue 字段转换为 defaultFormula 格式
 */
export const convertPropDefaultValueToPropDefaultFormula = (
  v: ApiResponse.CurrentUser.TemplateProp['defaultValue'],
): ApiResponse.CurrentUser.TemplateProp['defaultFormula'] => {
  if (typeof v === 'number') {
    return {
      type: 'const',
      const: v,
    };
  }

  if (v === 'createTime') {
    return PASS_ANY(CREATE_TIME()) as ApiResponse.CurrentUser.TemplateProp['defaultFormula'];
  } else if (v === 'creator') {
    return PASS_ANY(CREATOR()) as ApiResponse.CurrentUser.TemplateProp['defaultFormula'];
  } else if (v[0] === 'p') {
    return {
      type: 'prop',
      prop: Number(v.split('-')[1]),
    };
  } else if (v.split('-')[0] === 'status') {
    return PASS_ANY(
      STATUS_PROP(Number(v.split('-')[1])),
    ) as ApiResponse.CurrentUser.TemplateProp['defaultFormula'];
  } else
    return {
      type: 'const',
      const: v,
    };
};
